import memoizeOne from 'memoize-one'

import { getConfigValue } from './envHelpers'
import { getAuthToken, getOrganizationIdFromUrl } from './sessionHelpers'

export const getFileUrl = memoizeOne(
  (id: ID, params: { redirect?: 'false' | 'true'; download?: 'true' } = {}): string => {
    const url = new URL(`/file-uploads`, getConfigValue('VITE_QUOKKA_URL'))
    url.searchParams.set('organizationId', getOrganizationIdFromUrl())
    url.searchParams.set('id', id)
    url.searchParams.set('token', `${getAuthToken()}`)
    if (params.redirect) url.searchParams.set('redirect', params.redirect)
    if (params.download) url.searchParams.set('download', params.download)
    return url.toString()
  },
)

export const getSignedUrl = async (fileId: ID): Promise<string> => {
  const response = await fetch(getFileUrl(fileId, { redirect: 'false' }))
  const body = await response.json()
  return body.url
}

/**
 * Get data transfer file list.
 * @param  {Object} event Synthetic event Object
 * @return {Array}       List of files dropped
 */
export function getDataTransferItems(event: React.ChangeEvent<HTMLInputElement>): File[] {
  let dataTransferItemsList: File[] | DataTransferItem[] = []
  if (event.target && event.target.files) {
    dataTransferItemsList = Array.from(event.target.files)
  }

  // Convert from DataTransferItemsList to the native Array
  return Array.prototype.slice.call(dataTransferItemsList)
}

/**
 * Crop an image file to a square (center crop) and optionally resize it.
 * Uses the Canvas API to process the image client-side.
 *
 * @param file - The image file to crop
 * @param maxSize - Maximum width/height of the output (default 512px)
 * @returns A new File object with the cropped/resized image
 */
export async function cropImageToSquare(file: File, maxSize = 512): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(objectUrl)

      // Calculate the square crop dimensions (center crop)
      const size = Math.min(img.width, img.height)
      const offsetX = (img.width - size) / 2
      const offsetY = (img.height - size) / 2

      // Determine output size (don't upscale)
      const outputSize = Math.min(size, maxSize)

      // Create canvas and draw cropped image
      const canvas = document.createElement('canvas')
      canvas.width = outputSize
      canvas.height = outputSize

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }

      // Draw the center-cropped, resized image
      ctx.drawImage(
        img,
        offsetX,
        offsetY,
        size,
        size, // Source: center square
        0,
        0,
        outputSize,
        outputSize, // Destination: full canvas
      )

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create image blob'))
            return
          }

          // Create a new File with the cropped image
          const croppedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          })

          resolve(croppedFile)
        },
        'image/jpeg',
        0.9, // Quality
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Failed to load image'))
    }

    img.src = objectUrl
  })
}
